/**
 * MANAGEMENT (14) / Support WORKFLOW data layer — the NET-NEW helpdesk workflow hooks the Support SCREEN
 * uses, ADDITIVE to the shell's api.ts + support.api.ts (which own the list/badge reads + the chat/triage
 * writes). This file adds the four workflow surfaces: assignment (assign/reassign), validated
 * status-transition, resolution, and the SLA-breach detection sweep.
 *
 * Endpoints (all on the `platformRoute` chain, capability `management.support.manage`; verified against
 * server/src/modules/management/supportWorkflow.routes.js):
 *   PUT  /management/support/tickets/:id/assignment  assignTicket  → AssignTicketResult   (version-guarded)
 *   POST /management/support/tickets/:id/statut      changeStatut  → ChangeStatutResult   (illegal edge → 409)
 *   POST /management/support/tickets/:id/resolve     resolveTicket → ResolveTicketResult  (statut → resolu + note)
 *   POST /management/support/sla/sweep               runSlaSweep   → SlaSweepReport        (dry_run previews)
 *
 * WRITES invalidate the management root (managementKeys.root) so the inbox counts, per-row unread badges,
 * the cross-lab unread KPI, the open ticket detail AND the SLA preview all re-derive in lockstep. All follow
 * the shell's WRITE convention: `useApiMutation((vars) => api.<verb>(path, body), { invalidate })`.
 *
 * SLA SWEEP — exposed TWO ways:
 *   • useSlaSweepPreview(enabled)  — a READ hook (POST with dry_run=true) that queues NO mail: the strip's
 *     data source. Rooted in TanStack Query so it caches + re-derives on the management-root invalidation.
 *   • useRunSlaSweep()             — a MUTATION (POST, dry_run=false) the operator fires to actually queue
 *     the breach mail (idempotent per ticket server-side). Invalidates the preview + the inbox.
 */
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { apiRequest, useApiMutation } from "@/api";
import type { ApiError } from "@/api";
import { managementKeys } from "./api";
import type {
  AssignTicketResult,
  AssignTicketVars,
  ChangeStatutResult,
  ChangeStatutVars,
  ResolveTicketResult,
  ResolveTicketVars,
  SlaSweepReport,
} from "./supportWorkflow.types";

/** The SLA-preview key factory — extends `managementKeys` without re-rooting the namespace. */
export const supportWorkflowKeys = {
  slaPreview: (laboratoireId?: string) =>
    [...managementKeys.root, "support", "sla", "preview", laboratoireId ?? "all"] as const,
};

/** The SLA-preview poll cadence (ms). Config-driven (L-2) — the breach strip refreshes on this cadence. */
export const SUPPORT_SLA_PREVIEW_POLL_MS = 60_000;

/**
 * Assign or REASSIGN a ticket's triage owner (assigned_to=null unassigns). Version-guarded — a stale
 * version returns a typed 409 (CONFLICT); the screen surfaces "reload then retry". Invalidate the whole
 * management root so the inbox + the open ticket detail re-derive.
 */
export function useAssignTicket() {
  return useApiMutation<AssignTicketResult, AssignTicketVars>(
    ({ ticketId, ...body }) =>
      apiRequest(`/management/support/tickets/${ticketId}/assignment`, { method: "PUT", body }),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * Move a ticket along the ALLOWED lifecycle edges only. An illegal jump (e.g. ferme → resolu) is a typed
 * 409 BEFORE any write. Version-guarded. Invalidate the management root.
 */
export function useChangeStatut() {
  return useApiMutation<ChangeStatutResult, ChangeStatutVars>(
    ({ ticketId, ...body }) =>
      apiRequest(`/management/support/tickets/${ticketId}/statut`, { method: "POST", body }),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * Resolve a ticket: statut → resolu, stamp resolu_at, append the resolution note in-thread as an `admin`
 * message. Resolving a `ferme` (closed) ticket is a 409 (reopen first). Version-guarded. Invalidate the
 * management root so the inbox + the chat thread pick up the resolution message + the new statut.
 */
export function useResolveTicket() {
  return useApiMutation<ResolveTicketResult, ResolveTicketVars>(
    ({ ticketId, ...body }) =>
      apiRequest(`/management/support/tickets/${ticketId}/resolve`, { method: "POST", body }),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * The SLA-breach PREVIEW read: POST the sweep with dry_run=true so NO mail is queued — just the derived
 * breach report. The endpoint is a POST, but with dry_run=true it is a pure read, so it is rooted in
 * TanStack Query (caches + re-derives whenever a workflow write invalidates the management root) and polls
 * on {@link SUPPORT_SLA_PREVIEW_POLL_MS} while gated-on. Cross-lab (no laboratoire_id) by default.
 */
export function useSlaSweepPreview(enabled: boolean, laboratoireId?: string) {
  const key: QueryKey = supportWorkflowKeys.slaPreview(laboratoireId);
  return useQuery<SlaSweepReport, ApiError>({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest<SlaSweepReport>("/management/support/sla/sweep", {
        method: "POST",
        body: {},
        query: { dry_run: true, ...(laboratoireId ? { laboratoire_id: laboratoireId } : {}) },
        signal,
      }),
    enabled,
    staleTime: 15_000,
    ...(enabled ? { refetchInterval: SUPPORT_SLA_PREVIEW_POLL_MS } : {}),
  });
}

/**
 * The REAL SLA sweep (dry_run=false) — actually queues the breach mail (idempotent per ticket server-side).
 * A MUTATION the operator fires from the strip. Invalidates the management root so the preview + inbox
 * re-derive (a breached ticket now shows `emailQueued`).
 */
export function useRunSlaSweep() {
  return useApiMutation<SlaSweepReport, { laboratoire_id?: string }>(
    (vars) =>
      apiRequest("/management/support/sla/sweep", {
        method: "POST",
        body: {},
        ...(vars.laboratoire_id ? { query: { laboratoire_id: vars.laboratoire_id } } : {}),
      }),
    { invalidate: [managementKeys.root] },
  );
}
